import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import SucursalVentaSearch from '../SucursalVentaSearch';
import { SucursalVenta } from '../../services/sucursalesVentaService';
import type { SucursalUsuarioDraft } from '../../utils/syncSucursalesUsuario';

interface SucursalPredeterminadaAdminSectionProps {
  value: SucursalUsuarioDraft | null;
  onChange: (value: SucursalUsuarioDraft | null) => void;
  disabled?: boolean;
}

const SucursalPredeterminadaAdminSection: React.FC<SucursalPredeterminadaAdminSectionProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);

  const handleSelect = (sucursal: SucursalVenta) => {
    onChange({
      nroSuc: sucursal.nroSuc,
      nombreSucursal: sucursal.nombreSucursal?.trim() || `Sucursal ${sucursal.nroSuc}`,
      preferida: true,
    });
    setSearchOpen(false);
  };

  const label = value ? `${value.nombreSucursal} (${value.nroSuc})` : '';

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Los usuarios con rol Admin tienen acceso a todas las sucursales de venta. Opcionalmente puede
        definir una sucursal por defecto para autocompletar formularios de venta.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          fullWidth
          label="Sucursal por defecto"
          value={label}
          placeholder="Sin sucursal por defecto"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {value && (
                  <IconButton
                    onClick={() => onChange(null)}
                    disabled={disabled}
                    edge="end"
                    size="small"
                    aria-label="Quitar sucursal por defecto"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => setSearchOpen(true)}
                  disabled={disabled}
                  edge="end"
                  size="small"
                  aria-label="Buscar sucursal"
                >
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onClick={() => !disabled && setSearchOpen(true)}
          disabled={disabled}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Button
          variant="outlined"
          onClick={() => setSearchOpen(true)}
          disabled={disabled}
          sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          Elegir sucursal
        </Button>
      </Stack>

      <SucursalVentaSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelect}
      />
    </Box>
  );
};

export default SucursalPredeterminadaAdminSection;
